import { PublicClientApplication } from "@azure/msal-browser";
import { getIsTeamsEnvironment } from "../utils/environmentDetection";
import { useIsAuthenticated } from "@azure/msal-react";
import msalSetup from "./msalConfig";
import { getEnvironmentScope, getUtilitiesApiUrl } from "../utils";
import axios from "axios";

export const msalInstance = new PublicClientApplication(msalSetup);

/**
 * Checks if user is authenticated in Teams environment by looking for an active account
 * This is needed because useIsAuthenticated() doesn't work with Teams NAA authentication
 * @returns {boolean} True if user has an active account with username in Teams environment
 */
export const isTeamsAuthenticated = () => {
    let isAuthenticated = false;
  if (getIsTeamsEnvironment()) {
    const activeAccount = msalInstance.getActiveAccount();
    isAuthenticated = activeAccount && activeAccount.username;
  }
  return isAuthenticated;
};

export const useIsWebAuthenticated = () => {
    return useIsAuthenticated();
};

export const refreshToken = async () => {
  try {
    const scope = getEnvironmentScope();
    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.error("No active account found for token refresh");
      return null;
    }
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account: account,
      scopes: [scope],
      forceRefresh: true
    });
    if (tokenResponse && tokenResponse.accessToken) {
      sessionStorage.setItem("access_token", tokenResponse.accessToken);
      return tokenResponse.accessToken;
    } else {
      console.error("Token refresh failed, no access token returned");
      return null;
    }
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
};

/**
* Fetches configuration data from Azure blob via EVA API. Handles token expiry and retries once.
* @param {boolean} isRetry - Whether this is a retry after token refresh.
* @returns {Promise<Object>} The configuration data.
* @throws {Error} If unable to fetch config.
*/
export const getBlobData = async (isRetry = false) => {
  try {
    const token = sessionStorage.getItem('access_token');
    if (!token) throw new Error('Bearer token not found');
    const EVA_BASE_URL = getUtilitiesApiUrl();
    const response = await axios.get(
      `${EVA_BASE_URL}/api/v1/employees/me/configs`,
      {
        headers: {
          "Accept": "*/*",
          "X-USER-AGENT": "eva",
          "X-Tenant": "uhg",
          "X-portalsource": "azuread",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401 && !isRetry) {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          return await getBlobData(true); // retry once
        }
      } catch (refreshError) {
        console.error("Failed to refresh token:", refreshError);
      }
    }
    console.error("[getBlobData] Error fetching config from pullconfigs:", error);
    throw error;
  }
}